import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { RefObject } from "react"
import type { CombinedError } from "urql"
import { useShallow } from "zustand/react/shallow"

import type { QualityRequestSearchFilters } from "../stores/useQualityRequestSearchStore"
import { useQualityRequestSearchStore } from "../stores/useQualityRequestSearchStore"

import type { QualityApproval } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseQualityRequestListReturn {
  qualityRequests: QualityApproval[]
  isFetching: boolean
  error: CombinedError | Error | undefined
  sorting: SortingState
  searchParams: QualityRequestSearchFilters
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleScroll: () => void
  handleSortingChange: OnChangeFn<SortingState>
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof QualityRequestSearchFilters>(
    key: K,
    value: QualityRequestSearchFilters[K]
  ) => void
}

const ROUTE = "/app/dashboard/_layout/my-requests"

const FILTER_KEYS: (keyof QualityRequestSearchFilters)[] = ["status"]

export const useQualityRequestList = (): UseQualityRequestListReturn => {
  const store = useQualityRequestSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: qualityRequests,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
  } = useListPageBase<QualityApproval, QualityRequestSearchFilters>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
  })

  return {
    qualityRequests,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
  }
}
